import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
  ApiStoreList,
  ApiStoreDetail,
  ApiStoreXConf,
  ApiStoreXConfEnum,
  ApiPhysicalStoresOptions,
  ApiPhysicalStores
} from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.models';
import { ApiRequestService } from 'idt-ecommerce-api/_api/api-util/api-request.service';
import { DEFAULT_LANGUAGE_ID } from 'idt-ecommerce-api/stores/stores.models';


export interface ApiStoresServiceInterface {
  stores(languageId: string): Observable<ApiStoreList>;
  storeDetail(storeId: string, languageId: string): Observable<ApiStoreDetail>;
  storeXConf(storeId: string, languageId: string, configs: ApiStoreXConfEnum[]): Observable<ApiStoreXConf>;
  physicalStores(storeId: string, languageId: string, options: ApiPhysicalStoresOptions): Observable<ApiPhysicalStores>;
  retrieveDefaultStoreIdAndLanguageId(
    countryCode: string, languageCode: string
  ): Observable<{ storeId?: string; languageId: string }>;
}

@Injectable()
export class ApiStoresService implements ApiStoresServiceInterface {

  constructor(
    private readonly http: HttpClient,
    private readonly requestService: ApiRequestService
  ) {}

  public stores(languageId: string): Observable<ApiStoreList> {
    const iStoresJSON = sessionStorage.getItem('iStoresJSON');
    if (iStoresJSON) {
      return of(JSON.parse(iStoresJSON));
    }

    return this.requestService.brandId().pipe(
      map((brandId: string) => [{ key: 'brandId', value: brandId }]),
      switchMap((customParams) => this.requestService.requestUrl('catalog/store', languageId, customParams)),
      switchMap((url: string) => this.http.get<ApiStoreList>(url))
    );
  }

  public storeDetail(storeId: string, languageId?: string): Observable<ApiStoreDetail> {
    const iStoreJSON = sessionStorage.getItem('iStoreJSON');
    if (iStoreJSON) {
      return of(JSON.parse(iStoreJSON));
    }

    return this.requestService.requestUrl(`catalog/store/${storeId}`, languageId).pipe(
      switchMap((url: string) => this.http.get<ApiStoreDetail>(url))
    );
  }

  public storeXConf(storeId: string, languageId: string, configs: ApiStoreXConfEnum[]): Observable<ApiStoreXConf> {
    const customParams = configs.map((i) => ({ key: 'variable', value: i }));

    return this.requestService.requestUrl(`oam/store/${storeId}/xconfiguracion`, languageId, customParams).pipe(
      switchMap((url: string) => this.http.get<ApiStoreXConf>(url))
    );
  }

  public physicalStores(storeId: string, languageId: string, options: ApiPhysicalStoresOptions): Observable<ApiPhysicalStores> {
    const customParams = Object.entries(options).map((option) => ({ key: option[0], value: option[1] }));

    return this.requestService.requestUrl(`bam/store/${storeId}/physical-store`, languageId, customParams).pipe(
      switchMap((url: string) => this.http.get<ApiPhysicalStores>(url))
    );
  }

  public retrieveDefaultStoreIdAndLanguageId(countryCode: string, languageCode?: string): Observable<{storeId?: string; languageId: string}> {
    return this.stores(DEFAULT_LANGUAGE_ID).pipe(
      map(((stores) => {
        const defaultStore = stores.stores.find((store) => store.countryCode === countryCode);
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        const defaultLanguage = defaultStore?.supportedLanguages.find((lang) => lang.code === languageCode) ?? defaultStore?.supportedLanguages[0]!;
        return { storeId: defaultStore?.id.toString(), languageId: defaultLanguage?.id.toString() };
      }))
    );
  }

}
